// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

/**
 * NOTE (Vite):
 * - Use explicit .vue extensions for SFCs.
 * - Use @ alias (configured in vite.config.js) for cleaner imports.
 * - Fix known typo: SinglePropertyPage.vue -> SinglePropertyPage.vue
 */

const paths = {
    TrustedAgenciesPage: { en: '/trusted-agencies', bg: '/коректни-агенции' },
    AboutUsPage: { en: '/about-us', bg: '/за-нас' },
    TermsAndConditionsPage: { en: '/terms-and-conditions', bg: '/общи-условия' },
    PrivacyPolicyPage: { en: '/privacy-policy', bg: '/политика-за-поверителност' },

    SelectCleaningServicePage: { en: '/cleaning/services', bg: '/почистване/услуги' },
    SelectMovingServicePage: { en: '/moving/services', bg: '/преместване/услуги' },
    CleaningPage: { en: '/cleaning', bg: '/почистване' },
    MovingPage: { en: '/moving', bg: '/преместване' },
    InteriorPage: { en: '/interior', bg: '/интериор' },

    VendorDashboard: { en: '/vendor/dashboard', bg: '/доставчик/табло' },
    AgencyDashboard: { en: 'dashboard', bg: 'табло' },

    Profile: { en: '/profile', bg: '/профил' },
    AddProperty: { en: 'property', bg: '/имот' },
    EditProperty: { en: 'property/:id', bg: 'имот/:id' },
    AdminPropertyList: { en: 'properties', bg: 'имоти' },
    AdminListProducts: { en: 'products', bg: 'продукти' },
    AdminVendorProductsImport: { en: 'products-import', bg: 'импорт-на-продукти' },
    AdminVendorOrders: { en: 'orders', bg: 'поръчки' },
    AdminVendorSingleOrder: { en: 'orders/:id', bg: 'поръчки/:id' },
    AdminAddProduct: { en: 'product', bg: 'продукт' },
    ViewingRequests: { en: 'viewing-requests', bg: 'заявки-за-огледи' },
    UserEditProduct: { en: 'product/:id', bg: 'продукт/:id' },
    AdminRoommates: { en: 'roommates-posts', bg: 'обяви-за-съквартиранти' },
    EditRoommateOffer: { en: 'roommates-posts/:id', bg: 'обяви-за-съквартиранти/:id' },
    AdminSettings: { en: 'settings', bg: 'настройки' },

    MarketplaceProductCategories: { en: '/product/category', bg: '/продукти/категории' },
    MarketplaceProductPromoCategories: { en: '/product/category/promo', bg: '/продукти/категории/промоции' },
    SingleProductPage: { en: '/product/:id', bg: '/продукт/:id' },
    Cart: { en: '/cart', bg: '/покупки' },
    FavoriteProducts: { en: '/favorites', bg: '/любими' },

    PropertySearchPage: { en: '/properties', bg: '/имоти' },
    SinglePropertyPage: { en: '/property/:id', bg: '/имот/:id' },

    SearchRoommateResult: { en: '/roommates', bg: '/съквартиранти' },
    SingleRoommateOffer: { en: '/roommates/:id', bg: '/съквартиранти/:id' },
    AddRoommate: { en: '/roommates/post', bg: '/съквартиранти/обява' },

    Contacts: { en: '/contacts', bg: '/за-контакти' },
    SingleVendor: { en: '/vendor/:id', bg: '/вендор/:id' },
    User: { en: '/user/:id', bg: '/потребител/:id' },

    MarketplaceCategoryPage: { en: '/product/category/:id', bg: '/продукти/категория/:id' },
    MarketplaceCategoryPageKeywordSearch: { en: '/products', bg: '/продукти/' },

    Agency: { en: '/agency/:id', bg: '/агенция/:id' },
    Marketplace: { en: '/shop', bg: '/магазин' },
}

const lang = 'en' // keep your existing behavior; later you can wire this to i18n/store

function getPath(name) {
    return encodeURI(paths[name][lang])
}

const routes = [
    {
        path: getPath('TrustedAgenciesPage'),
        name: 'TrustedAgenciesPage',
        meta: { transition: 'slide-left' },
        component: () => import('@/views/TrustedAgenciesPage/TrustedAgenciesPage.vue'),
    },
    {
        path: getPath('AboutUsPage'),
        name: 'AboutUsPage',
        meta: { transition: 'slide-left' },
        component: () => import('@/views/AboutUsPage/AboutUsPage.vue'),
    },
    {
        path: getPath('TermsAndConditionsPage'),
        name: 'TermsAndConditionsPage',
        meta: { transition: 'slide-left' },
        component: () => import('@/views/TermsAndConditionsPage/TermsAndConditionsPage.vue'),
    },
    {
        path: getPath('PrivacyPolicyPage'),
        name: 'PrivacyPolicyPage',
        meta: { transition: 'slide-left' },
        component: () => import('@/views/PrivacyPolicyPage/PrivacyPolicyPage.vue'),
    },
    {
        path: '/coming-soon',
        name: 'ComingSoonPage',
        meta: { transition: 'slide-left' },
        component: () => import('@/views/ComingSoonPage/ComingSoonPage.vue'),
    },
    {
        path: getPath('SelectCleaningServicePage'),
        name: 'SelectCleaningServicePage',
        meta: { transition: 'slide-left' },
        component: () =>
            import('@/views/SelectCleaningServicePage/SelectCleaningServicePage.vue'),
    },
    {
        path: getPath('SelectMovingServicePage'),
        name: 'SelectMovingServicePage',
        meta: { transition: 'slide-left' },
        // NOTE: this is what you had (moving services uses SelectCleaningServicePage component)
        component: () =>
            import('@/views/SelectCleaningServicePage/SelectCleaningServicePage.vue'),
    },
    {
        path: getPath('MovingPage'),
        name: 'MovingPage',
        meta: { transition: 'slide-left' },
        // NOTE: kept your existing choice
        component: () => import('@/views/CleaningPage/CleaningPage.vue'),
    },
    {
        path: getPath('CleaningPage'),
        name: 'CleaningPage',
        meta: { transition: 'slide-left' },
        component: () => import('@/views/CleaningPage/CleaningPage.vue'),
    },
    {
        path: getPath('InteriorPage'),
        name: 'InteriorPage',
        meta: { transition: 'slide-left' },
        component: () => import('@/views/InteriorPage/InteriorPage.vue'),
    },

    // Profile + nested admin/vendor routes
    {
        path: getPath('Profile'),
        name: 'Profile',
        component: () => import('@/views/ProfileContainer/ProfileContainer.vue'),
        children: [
            {
                path: getPath('AddProperty'),
                name: 'AddProperty',
                component: () => import('@/views/AdminAddProperty/AdminAddProperty.vue'),
            },
            {
                path: getPath('EditProperty'),
                name: 'EditProperty',
                component: () => import('@/views/AdminAddProperty/AdminAddProperty.vue'),
            },
            {
                path: getPath('AdminPropertyList'),
                name: 'AdminPropertyList',
                component: () => import('@/views/AdminPropertyList/AdminPropertyList.vue'),
            },
            {
                path: getPath('VendorDashboard'),
                name: 'VendorDashboard',
                component: () =>
                    import('@/views/AdminDashboard/AdminDashboardVendor/AdminDashboardVendor.vue'),
            },
            {
                path: getPath('AgencyDashboard'),
                name: 'AgencyDashboard',
                component: () =>
                    import('@/views/AdminDashboard/AdminDashboardProperty/AdminDashboardProperty.vue'),
            },
            {
                path: getPath('AdminSettings'),
                name: 'AdminSettings',
                component: () => import('@/views/AdminSettings/AdminSettings.vue'),
            },
            {
                path: getPath('AdminAddProduct'),
                name: 'AdminAddProduct',
                component: () => import('@/views/AdminAddProduct/AdminAddProduct.vue'),
            },
            {
                path: getPath('UserEditProduct'),
                name: 'UserEditProduct',
                component: () => import('@/views/AdminAddProduct/AdminAddProduct.vue'),
            },
            {
                path: getPath('AdminListProducts'),
                name: 'AdminListProducts',
                component: () =>
                    import('@/views/AdminVendorProducts/AdminVendorProducts.vue'),
            },
            {
                path: getPath('AdminVendorProductsImport'),
                name: 'AdminVendorProductsImport',
                component: () =>
                    import('@/views/AdminVendorProductsImport/AdminVendorProductsImport.vue'),
            },
            {
                path: getPath('AdminVendorOrders'),
                name: 'AdminVendorOrders',
                component: () => import('@/views/AdminVendorOrders/AdminVendorOrders.vue'),
            },
            {
                path: getPath('AdminVendorSingleOrder'),
                name: 'AdminVendorSingleOrder',
                component: () =>
                    import('@/views/AdminVendorSingleOrder/AdminVendorSingleOrder.vue'),
            },
            {
                path: getPath('ViewingRequests'),
                name: 'ViewingRequests',
                component: () =>
                    import('@/views/AdminViewingRequests/AdminViewingRequests.vue'),
            },
            {
                path: getPath('AdminRoommates'),
                name: 'AdminRoommates',
                component: () => import('@/views/AdminRoommates/AdminRoommates.vue'),
            },
            {
                path: getPath('EditRoommateOffer'),
                name: 'EditRoommateOffer',
                component: () => import('@/views/AddRoommateOffer/AddRoommateOffer.vue'),
            },
        ],
    },

    // Marketplace categories
    {
        path: getPath('MarketplaceProductCategories'),
        name: 'MarketplaceProductCategories',
        component: () =>
            import('@/views/MarketplaceProductCategories/MarketplaceProductCategories.vue'),
    },
    {
        path: getPath('MarketplaceProductPromoCategories'),
        name: 'MarketplaceProductPromoCategories',
        component: () =>
            import('@/views/MarketplaceProductCategories/MarketplaceProductCategories.vue'),
    },

    // Blog
    {
        path: '/blog/category/:id?',
        name: 'BlogCategory',
        component: () => import('@/views/BlogCategoryPage/BlogCategoryPage.vue'),
    },
    {
        path: '/blog/:id',
        name: 'BlogSinglePost',
        component: () => import('@/views/BlogSinglePostPage/BlogSinglePostPage.vue'),
    },

    // Static/other pages
    {
        path: getPath('SingleVendor'),
        name: 'SingleVendor',
        component: () => import('@/views/SingleVendorPage/SingleVendorPage.vue'),
    },
    {
        path: getPath('Contacts'),
        name: 'Contacts',
        component: () => import('@/views/ContactPage/ContactPage.vue'),
    },
    {
        path: getPath('FavoriteProducts'),
        name: 'FavoriteProducts',
        component: () => import('@/views/FavoritesPage/Favorites.vue'),
    },
    {
        path: getPath('Cart'),
        name: 'Cart',
        component: () => import('@/views/Cart/Cart.vue'),
    },
    {
        path: getPath('SingleProductPage'),
        name: 'SingleProductPage',
        component: () => import('@/views/SingleProductPage/SingleProductPage.vue'),
    },

    // NOTE: kept your existing filename "MarketpaceCategoryPage" (spelling) to match your project
    {
        path: getPath('MarketplaceCategoryPage'),
        name: 'MarketplaceCategoryPage',
        component: () =>
            import('@/views/MarketplaceCategoryPage/MarketpaceCategoryPage.vue'),
    },
    {
        path: getPath('MarketplaceCategoryPageKeywordSearch'),
        name: 'MarketplaceCategoryPageKeywordSearch',
        component: () =>
            import('@/views/MarketplaceCategoryPage/MarketpaceCategoryPage.vue'),
    },

    {
        path: getPath('AddRoommate'),
        name: 'AddRoommate',
        component: () => import('@/views/AddRoommateOffer/AddRoommateOffer.vue'),
    },
    {
        path: getPath('Agency'),
        name: 'Agency',
        component: () => import('@/views/SingleAgencyPage/SingleAgencyPage.vue'),
    },
    {
        path: getPath('User'),
        name: 'User',
        component: () => import('@/views/SingleAgencyPage/SingleAgencyPage.vue'),
    },
    {
        path: getPath('SingleRoommateOffer'),
        name: 'SingleRoommateOffer',
        component: () =>
            import('@/views/SingleRoommateOffer/SingleRoommateOffer.vue'),
    },

    // Home
    {
        path: '/',
        name: 'Index',
        component: () => import('@/views/Index/Index.vue'),
    },
    {
        path: getPath('Marketplace'),
        name: 'Marketplace',
        component: () => import('@/views/Index/Index.vue'),
    },

    // Search
    {
        path: getPath('PropertySearchPage'),
        name: 'PropertySearchPage',
        component: () => import('@/views/SearchResult/SearchResult.vue'),
    },
    {
        path: getPath('SearchRoommateResult'),
        name: 'SearchRoommateResult',
        component: () =>
            import('@/views/SearchRoommateResult/SearchRoommateResult.vue'),
    },
    {
        path: getPath('SinglePropertyPage'),
        name: 'SinglePropertyPage',
        // FIXED: your old import referenced "SinglePropertyPage.vue"
        component: () =>
            import('@/views/SinglePropertyPage/SinglePropertyPage.vue'),
    },

    // Misc
    {
        path: '/user/confirmation',
        name: 'UserMailConfirmation',
        component: () => import('@/views/UserMailConfirmation.vue'),
    },
    {
        path: '/payment/status',
        name: 'PaymentStatus',
        meta: { showFooter: false },
        component: () => import('@/views/PaymentStatus.vue'),
    },

    // 404
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFoundPage',
        component: () => import('@/views/NotFound.vue'),
    },
]

const router = createRouter({
    history: createWebHistory(),
    scrollBehavior() {
        document.getElementById('app')?.scrollIntoView()
    },
    routes,
})

router.beforeEach((to, from, next) => {
    store.dispatch('utils/closeMarketplaceRoomsModal')
    next()
})

// router.afterEach((to) => {
//
//     setTimeout(() => {
//         if (typeof window.fbq === 'function') {
//             window.fbq('track', 'PageView')
//         }
//     }, 0)
// })

export default router